import React from "react";
import {
  Box,
  Button,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { IESDoc, FilterGroup, TableColumn } from "../../../../../app.interface";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { kitchenService } from "../../../services/kitchen.service";
import dayjs from "dayjs";
import { RecipeDetailsDrawer } from "./RecipeDetailsDrawer";
import { KitchenRecipe } from "./types";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

export const Recipes: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = React.useState<KitchenRecipe[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [sortField, setSortField] = React.useState("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [selectedRecipe, setSelectedRecipe] =
    React.useState<KitchenRecipe | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const filterGroups: FilterGroup[] = [
    {
      id: "category",
      label: "Category",
      type: "select",
      icon: "category",
      description: "Filter recipes by their category",
      options: [
        {
          id: "main_course",
          label: "Main Course",
          value: "main_course",
          icon: "restaurant",
          color: "#0891B2",
        },
        {
          id: "appetizer",
          label: "Appetizer",
          value: "appetizer",
          icon: "tapas",
          color: "#0891B2",
        },
        {
          id: "dessert",
          label: "Dessert",
          value: "dessert",
          icon: "cake",
          color: "#0891B2",
        },
        {
          id: "beverage",
          label: "Beverage",
          value: "beverage",
          icon: "local_bar",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter recipes by their status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: "#DC2626",
        },
      ],
    },
    {
      id: "date_field",
      label: "Date Field",
      type: "select",
      icon: "event",
      description: "Choose which date to filter by",
      options: [
        {
          id: "created_at",
          label: "Created Date",
          value: "created_at",
          icon: "add_circle",
          color: "#0891B2",
        },
        {
          id: "updated_at",
          label: "Updated Date",
          value: "updated_at",
          icon: "update",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort recipes by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "name",
      header: "Recipe Details",
      field: "name",
      type: "action",
      width: "250px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.description}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Last updated: {dayjs(row.updated_at).format("MMM D, YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      id: "category",
      header: "Category & Cost",
      field: "category",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>restaurant_menu</Icon>
            {row.category || "Uncategorized"}
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#059669", 0.08),
              color: "#059669",
              border: "1px solid",
              borderColor: alpha("#059669", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>payments</Icon>
            {row.total_cost?.toFixed(3)} BHD
          </Box>
        </Stack>
      ),
    },
    {
      id: "time",
      header: "Time & Servings",
      field: "total_preparing_time",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>timer</Icon>
            Prep: {row.total_preparing_time} min
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>microwave</Icon>
            Cook: {row.total_cooking_time} min
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>people</Icon>
            Serves: {row.servings}
          </Box>
        </Stack>
      ),
    },
    {
      id: "ingredients",
      header: "Ingredients & Steps",
      field: "ingredients",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>inventory_2</Icon>
            {row.ingredients.length} ingredients
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>format_list_numbered</Icon>
            {row.instructions.length} steps
          </Box>
          {row.tags.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {row.tags.slice(0, 2).map((tag: string, index: number) => (
                <Box
                  key={index}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: alpha("#F59E0B", 0.08),
                    color: "#F59E0B",
                    border: "1px solid",
                    borderColor: alpha("#F59E0B", 0.2),
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  {tag}
                </Box>
              ))}
              {row.tags.length > 2 && (
                <Tooltip title={row.tags.slice(2).join(", ")} arrow>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: alpha("#F59E0B", 0.08),
                      color: "#F59E0B",
                      border: "1px solid",
                      borderColor: alpha("#F59E0B", 0.2),
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    +{row.tags.length - 2}
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
        </Stack>
      ),
    },
    {
      id: "status",
      header: "Status",
      field: "is_active",
      type: "action",
      width: "150px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: row.is_active
                ? alpha("#059669", 0.08)
                : alpha("#DC2626", 0.08),
              color: row.is_active ? "#059669" : "#DC2626",
              border: "1px solid",
              borderColor: row.is_active
                ? alpha("#059669", 0.2)
                : alpha("#DC2626", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>
              {row.is_active ? "check_circle" : "cancel"}
            </Icon>
            {row.is_active ? "Active" : "Inactive"}
          </Box>
          {row.cost_history && row.cost_history.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Icon sx={{ fontSize: 14 }}>update</Icon>
              Cost updated:{" "}
              {dayjs(row.cost_history[0].timestamp).format("MMM D, YYYY")}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "120px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit Recipe" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.recipe.edit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Recipe" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.recipe.delete",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadRecipes = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await kitchenService.listRecipes({
        searchQuery: search || undefined,
        filters: [],
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        dateField: filters.date_field || "created_at",
        sortField,
        sortOrder,
        pageIndex,
        pageSize,
        category: filters.category !== "all" ? filters.category : undefined,
      });

      setRecipes(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      console.error("Error loading recipes:", error);
      toast.error("Failed to load recipes");
    } finally {
      setLoading(false);
    }
  }, [search, filters, sortField, sortOrder, pageIndex, pageSize]);

  React.useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleEdit = (recipe: KitchenRecipe) => {
    navigate(`/business/kitchen/recipes/${recipe._id}/edit`);
  };

  const handleDelete = async () => {
    if (!selectedRecipe) return;
    try {
      setSaving(true);
      const response = await kitchenService.deleteRecipe(selectedRecipe._id);
      if (response.data.success) {
        toast.success("Recipe deleted successfully");
        handleCloseDeleteDialog();
        loadRecipes();
      } else {
        toast.error("Failed to delete recipe");
      }
    } catch (error) {
      console.error("Error deleting recipe:", error);
      toast.error("Failed to delete recipe");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (recipe: KitchenRecipe) => {
    setSelectedRecipe(recipe);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedRecipe(null);
    setDeleteDialogOpen(false);
  };

  const handleDetailsOpen = (recipe: KitchenRecipe) => {
    setSelectedRecipe(recipe);
    setDetailsDrawerOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedRecipe(null);
    setDetailsDrawerOpen(false);
  };

  const handleAddRecipe = () => {
    navigate("/business/kitchen/recipes/new");
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Recipe Management"
        subtitle="Manage your kitchen's recipes and their ingredients"
        icon="restaurant_menu"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleAddRecipe}
            disabled={
              !hasPermission(
                "inventory.recipe.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Recipe
          </Button>
        }
      />

      <Box
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
        <AppSearch
          placeholder="Search recipes by name or description..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Box>

      <Box
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
        <AppTable
          data={recipes}
          columns={MasterColumns}
          loading={loading}
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          pagination={{
            total: totalItems,
            page: pageIndex,
            pageSize: pageSize,
            onPageChange: setPageIndex,
            onPageSizeChange: setPageSize,
          }}
          header={{
            title: "Recipes List",
            subtitle: "Your current list of recipes",
          }}
          onRowClick={(row) => handleDetailsOpen(row as KitchenRecipe)}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: loadRecipes,
            },
            export: {
              label: "Export Recipes",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "recipes-list",
              },
            },
          }}
          emptyState={{
            icon: "restaurant_menu",
            title: "No Recipes Found",
            description: search
              ? "No recipes match your search criteria. Try adjusting your filters or search term."
              : "Get started by adding your first recipe to manage your kitchen's menu effectively.",
            actionLabel: "Add Recipe",
            onAction: handleAddRecipe,
          }}
        />
      </Box>

      <RecipeDetailsDrawer
        open={detailsDrawerOpen}
        onClose={handleDetailsClose}
        recipe={selectedRecipe}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Recipe"
        itemName={selectedRecipe?.name || ""}
        itemDescription={selectedRecipe?.description || ""}
        loading={saving}
        icon="restaurant_menu"
      />
    </Box>
  );
};
